import type { NdaTemplate } from './storage'

/**
 * Default NDA Templates
 * These are pre-configured templates that tenants can use out of the box
 */

export const DEFAULT_NDA_TEMPLATES: Omit<NdaTemplate, 'id' | 'createdAt' | 'updatedAt' | 'updatedBy'>[] = [
  {
    name: 'Standard One-Way NDA',
    description: 'Standard unilateral NDA for protecting your confidential information during evaluations',
    type: 'one-way',
    industry: 'general',
    language: 'en',
    title: 'Non-Disclosure Agreement',
    text: `# Non-Disclosure Agreement

This Non-Disclosure Agreement (this "**Agreement**") is entered into as of the date of your acceptance below ("**Effective Date**") by and between the disclosing party whose information you are accessing ("**Disclosing Party**") and you, the individual or entity accessing such information ("**Receiving Party**").

## 1. Purpose and Scope

This Agreement governs the disclosure and use of Confidential Information (as defined below) that Disclosing Party may share with Receiving Party for the purpose of evaluating Disclosing Party's products, services, and business relationship opportunities (the "**Purpose**").

## 2. Definition of Confidential Information

"**Confidential Information**" means all information, materials, and data disclosed by Disclosing Party to Receiving Party, whether in written, oral, electronic, or visual form, including but not limited to:

- Technical documentation, specifications, architecture diagrams, and system designs
- Security policies, procedures, controls, and vulnerability assessments
- Compliance certifications, audit reports, and regulatory documentation
- Product roadmaps, feature plans, and development timelines
- Source code, algorithms, APIs, and proprietary software
- Customer lists, usage data, and business analytics
- Pricing information, financial data, and business strategies
- Any information clearly marked or identified as "Confidential," "Proprietary," or similar designation

Confidential Information also includes any notes, analyses, compilations, or derivatives created by Receiving Party that contain, reflect, or are based upon any of the foregoing.

## 3. Obligations of Receiving Party

Receiving Party agrees to:

**(a) Confidentiality.** Hold all Confidential Information in strict confidence and not disclose, publish, or disseminate such information to any third party without the prior written consent of Disclosing Party.

**(b) Limited Use.** Use Confidential Information solely for the Purpose and not for any other purpose, including without limitation competitive analysis, product development, or business advantage.

**(c) Protection.** Protect Confidential Information using the same degree of care used to protect its own confidential information of similar nature and importance, but in no event less than reasonable care.

**(d) Limited Access.** Limit access to Confidential Information to its employees, contractors, and advisors who (i) have a legitimate need to know for the Purpose, (ii) have been informed of the confidential nature of the information, and (iii) are bound by confidentiality obligations at least as protective as those contained herein.

**(e) No Reverse Engineering.** Not reverse engineer, disassemble, decompile, or otherwise attempt to derive the source code, underlying ideas, or algorithms of any software or technology constituting Confidential Information.

## 4. Exclusions from Confidential Information

The obligations set forth in Section 3 shall not apply to any information that Receiving Party can demonstrate:

**(a)** Was publicly available at the time of disclosure or thereafter becomes publicly available through no breach of this Agreement by Receiving Party;

**(b)** Was rightfully in Receiving Party's possession without confidentiality restrictions prior to receipt from Disclosing Party, as evidenced by Receiving Party's written records;

**(c)** Is independently developed by Receiving Party without use of or reference to Confidential Information, as evidenced by written records;

**(d)** Is rightfully received by Receiving Party from a third party without breach of any confidentiality obligation; or

**(e)** Is required to be disclosed by law, regulation, court order, or other governmental authority, provided that Receiving Party: (i) promptly notifies Disclosing Party of such requirement prior to disclosure, (ii) cooperates with Disclosing Party's efforts to obtain protective treatment of such information, and (iii) discloses only the minimum information required.

## 5. No License or Transfer of Rights

Nothing in this Agreement grants Receiving Party any license or right to Confidential Information except as expressly stated herein. All Confidential Information remains the exclusive property of Disclosing Party. No license under any patent, copyright, trademark, or other intellectual property right is granted or implied by this Agreement.

## 6. No Warranty

ALL CONFIDENTIAL INFORMATION IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.

## 7. Term and Termination

**(a) Term.** This Agreement shall commence on the Effective Date and continue for the duration of Receiving Party's access to Disclosing Party's trust center or information systems, plus an additional period of three (3) years thereafter.

**(b) Survival.** The confidentiality obligations set forth in Section 3 shall survive termination of this Agreement for a period of five (5) years.

**(c) Return or Destruction.** Upon termination of this Agreement or upon Disclosing Party's request, Receiving Party shall promptly: (i) return all Confidential Information and copies thereof to Disclosing Party, or (ii) destroy all Confidential Information and copies thereof and certify such destruction in writing to Disclosing Party. Notwithstanding the foregoing, Receiving Party may retain one (1) archival copy of Confidential Information solely for legal compliance purposes, subject to the continuing confidentiality obligations herein.

## 8. Remedies

Receiving Party acknowledges that unauthorized disclosure or use of Confidential Information may cause irreparable harm and significant injury to Disclosing Party, the extent of which may be difficult to ascertain. Accordingly, Receiving Party agrees that Disclosing Party shall have the right to seek immediate equitable relief, including injunctive relief and specific performance, in addition to any other remedies available at law or in equity, without the necessity of posting bond.

## 9. General Provisions

**(a) No Obligation.** Nothing in this Agreement obligates either party to proceed with any transaction or relationship, or to disclose any particular Confidential Information.

**(b) Entire Agreement.** This Agreement constitutes the entire agreement between the parties concerning the subject matter hereof and supersedes all prior or contemporaneous agreements, understandings, and communications, whether written or oral.

**(c) Amendment.** This Agreement may only be amended by a written instrument signed by both parties.

**(d) Governing Law.** This Agreement shall be governed by and construed in accordance with the laws of the jurisdiction of Disclosing Party's principal place of business, without regard to conflicts of law principles.

**(e) Severability.** If any provision of this Agreement is held invalid or unenforceable, the remaining provisions shall continue in full force and effect.

---

**BY CLICKING "I ACCEPT" OR "I AGREE" BELOW, YOU ACKNOWLEDGE THAT YOU HAVE READ THIS AGREEMENT, UNDERSTAND IT, AND AGREE TO BE BOUND BY ITS TERMS AND CONDITIONS. IF YOU ARE ACCEPTING ON BEHALF OF AN ORGANIZATION, YOU REPRESENT AND WARRANT THAT YOU HAVE THE AUTHORITY TO BIND THAT ORGANIZATION TO THIS AGREEMENT.**`,
    isDefault: true,
    isActive: true
  },
  {
    name: 'Mutual NDA',
    description: 'Two-way NDA for when both parties will be exchanging confidential information',
    type: 'mutual',
    industry: 'general',
    language: 'en',
    title: 'Mutual Non-Disclosure Agreement',
    text: `# Mutual Non-Disclosure Agreement

## 1. Parties and Purpose

This Mutual Non-Disclosure Agreement ("Agreement") is entered into between the parties for the purpose of exploring a potential business relationship and protecting confidential information that may be exchanged by both parties.

## 2. Confidential Information

Each party ("Disclosing Party") may disclose Confidential Information to the other party ("Receiving Party"). "Confidential Information" includes:
- Technical specifications, designs, and processes
- Business plans, strategies, and financial information
- Product roadmaps and development plans
- Customer data and usage information
- Security policies and compliance documentation
- Any information marked or identified as confidential

## 3. Mutual Obligations

Each Receiving Party agrees to:
- Keep all Confidential Information strictly confidential
- Use Confidential Information solely for the purpose of evaluating the potential business relationship
- Not disclose Confidential Information to third parties without prior written consent
- Protect Confidential Information with at least the same degree of care used for its own confidential information
- Not reverse engineer or attempt to derive source code or trade secrets

## 4. Permitted Disclosures

A Receiving Party may disclose Confidential Information to:
- Employees and contractors who have a legitimate need to know
- Legal and financial advisors bound by professional confidentiality obligations

Provided that such persons are informed of the confidential nature and agree to be bound by these terms.

## 5. Exclusions

Obligations under this Agreement do not apply to information that:
- Is or becomes publicly available without breach of this Agreement
- Was already known to the Receiving Party without restriction
- Is independently developed without use of Confidential Information
- Is lawfully obtained from a third party without restriction
- Must be disclosed pursuant to law, regulation, or court order (with prior notice to Disclosing Party)

## 6. No License or Warranty

- No license or rights are granted under any intellectual property
- All Confidential Information is provided "AS IS" without warranty
- Neither party is obligated to proceed with any business relationship

## 7. Term and Termination

- This Agreement is effective upon acceptance and continues for 3 years
- Confidentiality obligations survive termination for 5 years
- Upon termination, each party shall return or destroy all Confidential Information

## 8. Equitable Relief

Each party acknowledges that breach may cause irreparable harm and that monetary damages may be inadequate. Each party agrees that the other may seek equitable relief, including injunction, in addition to other remedies.

By accepting this Agreement, both parties acknowledge they have read, understood, and agree to be bound by these mutual terms.`,
    isDefault: false,
    isActive: true
  },
  {
    name: 'Evaluation NDA',
    description: 'Short-form NDA specifically for software evaluation and proof-of-concept projects',
    type: 'evaluation',
    industry: 'technology',
    language: 'en',
    title: 'Evaluation Non-Disclosure Agreement',
    text: `# Evaluation Non-Disclosure Agreement

## Purpose

This Agreement protects confidential information shared during your evaluation of our software and services.

## What You'll See

During evaluation, you may access:
- Product documentation and user guides
- Security and compliance certifications
- Architecture diagrams and technical specifications
- Demo environments and test data
- API documentation and integration guides

## Your Responsibilities

You agree to:
- Keep all materials confidential and use them only for evaluation purposes
- Not share access credentials or download materials with unauthorized persons
- Not use our confidential information to develop competing products
- Notify us immediately of any unauthorized access or disclosure
- Return or delete all materials when evaluation ends

## What's Not Covered

These confidentiality obligations don't apply to information that:
- Is already public or becomes public through no fault of yours
- You already knew before we shared it
- You developed independently
- You're required by law to disclose (with notice to us)

## Duration

- This Agreement covers your evaluation period plus 1 year after
- You can terminate evaluation at any time by returning/deleting all materials
- Our confidentiality rights survive termination

## Important Notes

- This is an evaluation only - no license or rights are granted
- We make no warranties about materials shared
- We're not obligated to enter into any business relationship

## Contact

Questions about this agreement? Contact us at the email address provided in your evaluation invitation.

By accepting, you confirm you've read and agree to these terms.`,
    isDefault: false,
    isActive: true
  },
  {
    name: 'Vendor NDA',
    description: 'NDA for vendors and service providers accessing your systems or data',
    type: 'vendor',
    industry: 'general',
    language: 'en',
    title: 'Vendor Non-Disclosure Agreement',
    text: `# Vendor Non-Disclosure Agreement

## 1. Scope

This Agreement governs your access to our confidential information and systems as a vendor or service provider.

## 2. Confidential Information

As a vendor, you may access or receive:
- Customer data and personal information
- System architecture and security configurations
- Internal processes and procedures
- Business strategies and financial information
- Source code, APIs, and technical documentation
- Any other information marked or identified as confidential

## 3. Vendor Obligations

You agree to:
- Use confidential information only to perform contracted services
- Implement appropriate security measures to protect our information
- Not use our information for your own benefit or that of other clients
- Limit access to personnel who have a legitimate need to know
- Ensure all personnel sign confidentiality agreements
- Report any security incidents or breaches immediately
- Not retain copies after service completion without our written consent
- Comply with all applicable privacy and security regulations (GDPR, CCPA, etc.)

## 4. Data Protection

If you process personal data on our behalf:
- You act as a data processor under applicable privacy laws
- You must comply with our data processing agreement
- You must implement appropriate technical and organizational measures
- You must assist us with data subject requests and regulatory compliance
- You must not subcontract processing without our prior written consent

## 5. Security Requirements

You must:
- Encrypt data in transit and at rest
- Maintain SOC 2 Type II or equivalent certification (if applicable)
- Conduct regular security assessments and penetration testing
- Provide evidence of compliance upon request
- Maintain cyber liability insurance (minimum $2M coverage)

## 6. Audit Rights

We reserve the right to:
- Audit your security controls and practices annually
- Request evidence of compliance at any time
- Terminate immediately if security standards are not met

## 7. Breach Notification

You must notify us within 24 hours of discovering:
- Any unauthorized access to our confidential information
- Any data breach or security incident
- Any subpoena or legal request for our information

## 8. Term and Termination

- This Agreement is effective from your acceptance and continues during our business relationship
- Obligations survive termination for 7 years
- Upon termination, you must immediately return or securely destroy all confidential information and provide written certification

## 9. Indemnification

You agree to indemnify us against all losses, damages, and costs arising from:
- Your breach of this Agreement
- Your negligence or willful misconduct
- Your violation of applicable laws or regulations

By accepting this Agreement, you confirm that you have the authority to bind your organization and agree to these vendor terms.`,
    isDefault: false,
    isActive: true
  },
  {
    name: 'Simple NDA',
    description: 'Streamlined, easy-to-read NDA for quick document access',
    type: 'one-way',
    industry: 'general',
    language: 'en',
    title: 'Simple Non-Disclosure Agreement',
    text: `# Simple Non-Disclosure Agreement

## The Basics

We're about to share confidential information with you. This agreement ensures you'll keep it confidential.

## What's Confidential?

Confidential information includes:
- Documents you access in this trust center
- Security policies, certifications, and audit reports
- Technical specifications and architecture
- Anything marked "Confidential" or "Proprietary"

## What You Agree To

✓ Keep confidential information private
✓ Use it only to evaluate our services
✓ Don't share it with anyone else without permission
✓ Protect it like you'd protect your own confidential information
✓ Delete or return it when requested

## What's NOT Confidential

Information that:
- Is already public knowledge
- You already knew before we shared it
- You figured out on your own
- Must be disclosed by law (but please notify us first)

## How Long?

This agreement lasts for 2 years from today or until you stop accessing our trust center, whichever is longer.

## Questions?

Contact us at the support email listed in our trust center.

By clicking "I Accept" below, you agree to keep our confidential information confidential.`,
    isDefault: false,
    isActive: true
  }
]

/**
 * Get a template by type and industry
 */
export function getTemplateByType(type: NdaTemplate['type'], industry?: string): Omit<NdaTemplate, 'id' | 'createdAt' | 'updatedAt' | 'updatedBy'> | undefined {
  return DEFAULT_NDA_TEMPLATES.find(t =>
    t.type === type &&
    (!industry || !t.industry || t.industry === industry || t.industry === 'general')
  )
}

/**
 * Get the default template
 */
export function getDefaultTemplate(): Omit<NdaTemplate, 'id' | 'createdAt' | 'updatedAt' | 'updatedBy'> {
  return DEFAULT_NDA_TEMPLATES.find(t => t.isDefault) || DEFAULT_NDA_TEMPLATES[0]
}
